import React, {useEffect, useRef, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import axios from 'axios';
import {Badge} from 'primereact/badge';
import moment from 'moment';

const INACTIVE_STATUS = 'INACTIVE';

const getStatusColor = (status) => {
  switch (status) {
    case 'NEW':
      return '#8e3ec7';
    case 'SCHEDULED':
      return '#6c757d';
    case 'IN_PROGRESS':
      return '#0ea5e9';
    case 'PAUSED':
      return '#f59e0b';
    case 'SUSPENDED':
      return '#f59e0b';
    case 'DONE':
      return '#22c55e';
    case 'DELETED':
      return '#ef4444';
    case INACTIVE_STATUS:
      return '#6c757d';
    default:
      return '#78909c';
  }
};

const getStatusBackgroundColor = (status) => {
  switch (status) {
    case 'NEW':
      return 'rgba(142,62,199,0.18)';
    case 'SCHEDULED':
      return 'rgba(108,117,125,0.16)';
    case 'IN_PROGRESS':
      return 'rgba(14,165,233,0.18)';
    case 'PAUSED':
    case 'SUSPENDED':
      return 'rgba(245,158,11,0.20)';
    case 'DONE':
      return 'rgba(34,197,94,0.18)';
    case 'DELETED':
      return 'rgba(239,68,68,0.18)';
    case INACTIVE_STATUS:
      return 'rgba(108,117,125,0.16)';
    default:
      return 'rgba(120,144,156,0.12)';
  }
};

const getStatusLabel = (status, productionStatuses = [], t = (key) => key) => {
  if (status === INACTIVE_STATUS || !status) {
    return t('inactive');
  }

  const dictItem = productionStatuses.find(item => item.key === status);
  return dictItem?.value || status || '-';
};

const getStatusSeverity = (status) => {
  switch (status) {
    case 'NEW':
      return 'primary';
    case 'SCHEDULED':
    case INACTIVE_STATUS:
      return 'secondary';
    case 'IN_PROGRESS':
      return 'info';
    case 'PAUSED':
    case 'SUSPENDED':
      return 'warning';
    case 'DONE':
      return 'success';
    case 'DELETED':
      return 'danger';
    default:
      return 'secondary';
  }
};

const renderStatusBadge = (status, productionStatuses = [], t = (key) => key) => {
  return (
    <Badge
      value={getStatusLabel(status, productionStatuses, t)}
      severity={getStatusSeverity(status)}
    />
  );
};

const ProductionstatusFloorplanComponent = (props) => {
  const {t, floorPlanId} = props;
  const LAST_FLOOR_PLAN_STORAGE_KEY = 'productionStatusLastFloorPlanId';
  const effectiveFloorPlanId = floorPlanId || localStorage.getItem(LAST_FLOOR_PLAN_STORAGE_KEY);
  const [floorPlan, setFloorPlan] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [zoom, setZoom] = useState(1);
  const stageRef = useRef(null);
  const [machineStatuses, setMachineStatuses] = useState({});
  const [productionStatuses, setProductionStatuses] = useState([]);

  const commonHeaders = {
    'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
    'App-Network': localStorage.getItem("networkId")
  };

  const getJobStatusCode = (job) => {
    return (
      job?.production_status?.code ||
      job?.production_status?.key ||
      job?.production_status?.value ||
      job?.production_status ||
      job?.status?.code ||
      job?.status?.key ||
      job?.status?.value ||
      job?.status ||
      null
    );
  };

  const getJobMachineId = (job) => {
    return (
      job?.machine?.id ||
      job?.production_item?.machine?.id ||
      job?.machine_id ||
      null
    );
  };

  const getMachineStatus = (item) => {
    const machineId = item?.machine?.id;
    return machineId ? machineStatuses[machineId] : null;
  };

  const getMachineTask = (machineStatus) => machineStatus?.task || null;

  const getMachineStatusCode = (machineStatus) => {
    return machineStatus?.machine_status || INACTIVE_STATUS;
  };

  const formatMachineLogDate = (machineStatus) => {
    const issueDate = machineStatus?.machine_log?.issue_date;
    return issueDate ? moment.utc(issueDate).local().format('YYYY-MM-DD HH:mm:ss') : '-';
  };

  const getMachineLogEmployeeName = (machineStatus) => {
    return machineStatus?.machine_log?.employee?.name || '-';
  };

  const getJobProductionItemName = (job) => {
    return job?.production_item?.name || '-';
  };

  const getJobExternalReference = (job) => {
    return job?.production_item?.external_reference || '-';
  };

  const getJobProjectNumber = (job) => {
    return (
      job?.project_number ||
      job?.project?.project_number ||
      job?.production_item?.project_number ||
      job?.production_item?.project?.project_number ||
      '-'
    );
  };

  const getJobProjectName = (job) => {
    return (
      job?.project?.name ||
      job?.production_item?.project?.name ||
      '-'
    );
  };

  const fetchMachineStatuses = async (floorPlanData = floorPlan) => {
    const placedMachines = floorPlanData?.floor_plan_machines || [];
    const ids = placedMachines
      .map(item => item.machine?.id)
      .filter(Boolean);

    if (ids.length === 0) {
      setMachineStatuses({});
      return;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST}/api/v2/production-job/machine-status`,
        { ids },
        { headers: commonHeaders }
      );
      const jobs = response?.data?.data?.items || response?.data?.data || [];
      const statusMap = {};
      jobs.forEach(machineStatus => {
        const machineId = getJobMachineId(machineStatus);
        if (machineId) {
          statusMap[machineId] = machineStatus;
        }
      });
      setMachineStatuses(statusMap);
    } catch (error) {
      console.error("Gép státusz lekérési hiba", error);
    }
  };

  const fetchImageBlob = async (fileId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/file/${fileId}/download`, {
        headers: {
          'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
          'App-Network': localStorage.getItem("networkId")
        },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setImageBlobUrl(url);
    } catch (err) {
      console.error("Kép letöltési hiba", err);
    }
  };

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/dictionary/production-status`, {
      headers: commonHeaders
    })
      .then(response => {
        setProductionStatuses(response?.data?.data?.items || response?.data?.data || []);
      })
      .catch(error => {
        window.App.toastShow('error', t('error'), error.toString(), '', error.response);
      });
  }, []);

  useEffect(() => {
    if (!floorPlan?.floor_plan_machines?.length) {
      setMachineStatuses({});
      return;
    }
    fetchMachineStatuses(floorPlan);
    const interval = setInterval(() => {
      fetchMachineStatuses(floorPlan);
    }, 10000);
    return () => clearInterval(interval);
  }, [floorPlan]);

  useEffect(() => {
    if (!effectiveFloorPlanId) {
      setFloorPlan(null);
      setImageBlobUrl('');
      return;
    }
    localStorage.setItem(LAST_FLOOR_PLAN_STORAGE_KEY, effectiveFloorPlanId);
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_HOST}/api/v2/floor-plan/${effectiveFloorPlanId}`, {
      headers: {
        'Authorization': 'Bearer ' + localStorage.getItem("userToken"),
        'App-Network': localStorage.getItem("networkId")
      }
    })
      .then(response => {
        const data = response?.data?.data;
        setFloorPlan(data || null);
        if (data?.file?.id) {
          fetchImageBlob(data.file.id);
        }
      })
      .catch(error => {
        console.error("Hiba az alaprajz részleteinek betöltésekor", error);
        setFloorPlan(null);
      })
      .finally(() => setLoading(false));
    return () => {
      if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
    };
  }, [effectiveFloorPlanId]);

  const zoomIn = () => setZoom(prev => Math.min(3, Number((prev + 0.1).toFixed(2))));
  const zoomOut = () => setZoom(prev => Math.max(0.4, Number((prev - 0.1).toFixed(2))));
  const resetZoom = () => setZoom(1);

  if (loading) return <div className="p-p-4 p-text-center">{t('loading')}</div>;
  if (!floorPlan) return <div className="p-p-4 p-text-center">{t('selectFloorPlanToView')}</div>;

  const machines = floorPlan?.floor_plan_machines || [];
  const selectedMachineStatus = selectedItem ? getMachineStatus(selectedItem) : null;
  const selectedTask = getMachineTask(selectedMachineStatus);

  return (
    <div style={{position: 'relative'}}>
      <div className="p-grid p-ai-center p-mb-3">
        <div className="p-col-12 p-lg-4">
          <h3 className="p-m-0 text-primary">
            {floorPlan?.name || t('productionStatus')}
          </h3>
        </div>

        <div className="p-col-12 p-lg-8 p-text-left p-text-lg-right">
          <div style={{display: 'inline-flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap'}}>
            <Button type="button" icon="pi pi-search-minus" className="p-button-secondary" onClick={zoomOut} />
            <Button type="button" label={`${Math.round(zoom * 100)}%`} className="p-button-secondary" onClick={resetZoom} />
            <Button type="button" icon="pi pi-search-plus" className="p-button-secondary" onClick={zoomIn} />
          </div>
        </div>
      </div>

      <div
        style={{
          width: '100%',
          height: '70vh',
          minHeight: '500px',
          maxHeight: '900px',
          border: '1px solid #dcdfe6',
          background: '#f8f9fa',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <div
          style={{
            width: `${zoom * 100}%`,
            transformOrigin: 'top left',
            position: 'relative'
          }}
        >
          <div
            ref={stageRef}
            style={{
              position: 'relative',
              width: '100%',
              cursor: 'default',
              touchAction: 'none'
            }}
          >
            <img
              src={imageBlobUrl || ''}
              alt={floorPlan?.name || t('floorPlan')}
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            />

            <div style={{position: 'absolute', inset: 0, zIndex: 2}}>
              {machines.map(item => {
                const machineStatus = getMachineStatus(item);
                const task = getMachineTask(machineStatus);
                const statusCode = getMachineStatusCode(machineStatus);
                return (
                  <div
                    key={item.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSelectedItem(item);
                      setDialogVisible(true);
                    }}
                    style={{
                      position: 'absolute',
                      left: (item.dimension_x * 100) + '%',
                      top: (item.dimension_y * 100) + '%',
                      width: (item.width * 100) + '%',
                      height: (item.height * 100) + '%',
                      border: `3px solid ${getStatusColor(statusCode)}`,
                      backgroundColor: getStatusBackgroundColor(statusCode),
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      overflow: 'visible'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: '#111',
                        background: 'rgba(255,255,255,0.88)',
                        padding: '2px 6px',
                        marginBottom: '4px',
                        borderRadius: '4px',
                        maxWidth: 'calc(100% - 8px)',
                        textAlign: 'center',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        pointerEvents: 'none'
                      }}
                    >
                      <div>{item.machine?.name || '-'}</div>
                      <div style={{fontSize: '11px', fontWeight: 500, marginTop: '2px'}}>
                        {machineStatus?.machine_log?.operation || t('inactive')}
                      </div>
                      {task && (
                        <>
                          <div style={{marginTop: '2px'}}>
                            {t('taskStatus')}: {renderStatusBadge(getJobStatusCode(task), productionStatuses, t)}
                          </div>
                          <div style={{fontSize: '11px', fontWeight: 500}}>
                            {getJobProductionItemName(task)}
                          </div>
                          <div style={{fontSize: '11px', fontWeight: 500}}>
                            {getJobExternalReference(task)}
                          </div>
                          <div style={{fontSize: '11px', fontWeight: 500}}>
                            {getJobProjectNumber(task)}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="p-mt-3">
        <h4 className="p-mb-2">{t('placedMachines')}</h4>
        {machines.length === 0 && (
          <div style={{color: '#666'}}>{t('noMachinesOnFloorPlan')}</div>
        )}
        <div className="p-grid">
          {machines.map((item) => {
            const machineStatus = getMachineStatus(item);
            const task = getMachineTask(machineStatus);
            const statusCode = getMachineStatusCode(machineStatus);
            return (
              <div key={item.id} className="p-col-12 p-md-4">
                <div
                  style={{
                    padding: '10px',
                    border: `1px solid ${getStatusColor(statusCode)}`,
                    borderLeft: `5px solid ${getStatusColor(statusCode)}`,
                    background: getStatusBackgroundColor(statusCode),
                    borderRadius: '4px',
                    marginBottom: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}
                >
                  <b>{item.machine?.name || t('unknownMachine')}</b>

                  <div style={{fontSize: '12px', color: '#666', marginTop: '4px'}}>
                    {t('lastOperation')}: {machineStatus?.machine_log?.operation || '-'}
                  </div>
                  <div style={{fontSize: '12px', color: '#666'}}>
                    {t('lastOperationTime')}: {formatMachineLogDate(machineStatus)}
                  </div>
                  <div style={{fontSize: '12px', color: '#666'}}>
                    {t('employee')}: {getMachineLogEmployeeName(machineStatus)}
                  </div>

                  {task && (
                    <div style={{borderTop: '1px solid rgba(0,0,0,0.12)', marginTop: '8px', paddingTop: '8px'}}>
                      <div style={{fontSize: '12px', color: '#666'}}>
                        {t('taskStatus')}: {renderStatusBadge(getJobStatusCode(task), productionStatuses, t)}
                      </div>
                      <div style={{fontSize: '12px', color: '#666'}}>
                        {t('task')}: {getJobProductionItemName(task)}
                      </div>
                      <div style={{fontSize: '12px', color: '#666'}}>
                        {t('externalReference')}: {getJobExternalReference(task)}
                      </div>
                      <div style={{fontSize: '12px', color: '#666'}}>
                        {t('project')}: {getJobProjectNumber(task)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Dialog
        header={selectedItem?.machine?.name || t('machineDetails')}
        visible={dialogVisible}
        style={{width: '30vw'}}
        modal
        onHide={() => setDialogVisible(false)}
      >
        {selectedItem && (
          <div className="p-fluid">
            <div className="field p-mb-3">
              <label><b>{t('fieldName')}</b></label>
              <div>{selectedItem.machine?.name || '-'}</div>
            </div>
            <div className="field p-mb-3">
              <h4>{t('machineInformation')}</h4>
              <div className="field p-mb-3">
                <label><b>{t('lastOperation')}</b></label>
                <div>{selectedMachineStatus?.machine_log?.operation || '-'}</div>
              </div>
              <div className="field p-mb-3">
                <label><b>{t('lastOperationTime')}</b></label>
                <div>{formatMachineLogDate(selectedMachineStatus)}</div>
              </div>
              <div className="field p-mb-3">
                <label><b>{t('employee')}</b></label>
                <div>{getMachineLogEmployeeName(selectedMachineStatus)}</div>
              </div>
              {selectedTask && (
                <>
                  <h4 style={{borderTop: '1px solid #ddd', paddingTop: '12px'}}>{t('taskInformation')}</h4>
                  <div className="field p-mb-3">
                    <label><b>{t('taskStatus')}</b></label>
                    <div>{renderStatusBadge(getJobStatusCode(selectedTask), productionStatuses, t)}</div>
                  </div>
                  <div className="field p-mb-3">
                    <label><b>{t('task')}</b></label>
                    <div>{getJobProductionItemName(selectedTask)}</div>
                  </div>

                  <div className="field p-mb-3">
                    <label><b>{t('externalReference')}</b></label>
                    <div>{getJobExternalReference(selectedTask)}</div>
                  </div>

                  <div className="field p-mb-3">
                    <label><b>{t('projectNumber')}</b></label>
                    <div>{getJobProjectNumber(selectedTask)}</div>
                  </div>

                  <div className="field p-mb-3">
                    <label><b>{t('columnProjectName')}</b></label>
                    <div>{getJobProjectName(selectedTask)}</div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default withTranslation('common')(ProductionstatusFloorplanComponent);
